/**
 * The filter-bar kit of UI spec §3.6.
 *
 * A white strip above the content: 1px border, radius 6px, `9px 12px` padding,
 * flex-wrapping with a 9px gap, and each control a 10.5px uppercase micro-label
 * above a 5–7px padded input of `min-width: 112px`, with the actions cluster
 * right-aligned.
 *
 * ## Why this is shared rather than per-screen
 *
 * The Calendar_View and the Initiative list each grew their own copy of this
 * anatomy — the same strip classes, the same `Control` component and the same
 * control class string, duplicated verbatim. Four Manpower screens then grew four
 * more copies of the control string alone, which had already drifted: a 12px
 * filter control and a 12.5px form control disagreeing on padding and on the focus
 * ring. One module owns the vocabulary so a screen adopts it rather than
 * re-deriving it, and a token change lands everywhere at once.
 *
 * ## What this deliberately does not own
 *
 * **State.** §3.6's "one filter bar drives everything on the page" means the bar
 * holds nothing: every control reports upward and the page owns the value, so the
 * table, any KPI row and the export all read one source. Every component here is
 * controlled.
 *
 * **Its own bottom margin.** §3.6 specifies 14px below the strip, but the screens
 * that use this lay their blocks out in a `flex flex-col gap-*` column, where a
 * margin here would be added to the parent's gap and space the bar twice. The
 * parent's gap is the single spacing authority; see the `Field` primitive for the
 * same trap resolved the same way.
 *
 * **Copy.** Every label is passed in already resolved from the catalogue (R18.9),
 * so this module carries no user-facing text.
 */
import * as React from 'react';
import { useId } from 'react';
import { cn } from '@/lib/utils';

/**
 * The canonical filter-control class (§3.6): the padding, radius, 12px type and
 * accent focus ring every select, search box and multi-select trigger in a filter
 * bar shares.
 *
 * Exported because a control this kit does not wrap — a Radix dropdown trigger,
 * say — still has to look identical to the ones it does.
 */
export const FILTER_CONTROL_CLASS =
  'border-line bg-panel text-txt min-w-[112px] rounded-[var(--radius-control)] border px-[7px] py-[5px] text-[12px] outline-none focus-visible:border-accent-line';

export interface FilterBarProps {
  /**
   * The accessible name of the strip, already resolved from the catalogue. The
   * bar is a `search` landmark, so it needs a name to be distinguishable from any
   * other landmark on the page.
   */
  readonly label: string;
  readonly className?: string;
  readonly children: React.ReactNode;
}

/** The filter strip itself (§3.6). */
export function FilterBar({ label, className, children }: FilterBarProps): React.JSX.Element {
  return (
    <div
      role="search"
      aria-label={label}
      className={cn(
        'border-line bg-panel flex flex-wrap items-end gap-[9px] rounded-[var(--radius-card)] border px-3 py-[9px]',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The right-aligned actions cluster (§3.6's `Reset · Save view · primary Export`).
 *
 * `ml-auto` rather than a spacer element, so the cluster still lands right when
 * the controls before it have wrapped onto several lines.
 */
export function FilterActions({
  className,
  children,
}: {
  readonly className?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={cn('ml-auto flex items-end gap-2 self-end', className)}>{children}</div>
  );
}

/**
 * One labelled control: the micro-label above the input.
 *
 * `children` is a render prop handed the generated id, so the control it returns
 * adopts that id and the `<label htmlFor>` names something focusable (R18.6). A
 * wrapping `<label>` with an implicit association would leave the label pointing
 * at nothing when the control is a composite.
 */
export function FilterControl({
  label,
  className,
  children,
}: {
  readonly label: string;
  /** `undefined` is explicit in the type because `FilterSelect` forwards its own optional value (`exactOptionalPropertyTypes`). */
  readonly className?: string | undefined;
  readonly children: (id: string) => React.ReactNode;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className={cn('flex min-w-[112px] flex-col gap-1', className)}>
      <label htmlFor={id} className="text-label text-[10.5px] font-semibold uppercase tracking-wide">
        {label}
      </label>
      {children(id)}
    </div>
  );
}

/** A labelled single-select filter: {@link FilterControl} around a `<select>`. */
export function FilterSelect({
  label,
  value,
  onChange,
  disabled = false,
  className,
  children,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <FilterControl label={label} className={className}>
      {(id) => (
        <select
          id={id}
          className={FILTER_CONTROL_CLASS}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {children}
        </select>
      )}
    </FilterControl>
  );
}

/**
 * A checkbox filter.
 *
 * No micro-label above it: the checkbox's own label is its name, so a second one
 * would repeat it. `self-end` with the control's own vertical padding keeps the
 * box on the same baseline as the inputs beside it in an `items-end` strip.
 */
export function FilterCheckbox({
  label,
  checked,
  onChange,
  className,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly className?: string;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className={cn('flex items-center gap-2 self-end py-[5px]', className)}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id} className="text-[12px] text-txt">
        {label}
      </label>
    </div>
  );
}
